import { c as _c } from "react/compiler-runtime";
import { Box, Typography, useTheme } from "@mui/material";
import { type EdgeData, selectNodeDataById } from "#/entities/dataStructures/node/model/nodeSlice";
import { getNodeColors } from "#/shared/lib";
import { useAppSelector } from "#/store/hooks";
const NODE_OFFSET = 21;
const THICKNESS = 4;
export type GraphEdgeProps = EdgeData & {
  treeName: string;
};
export const GraphEdge: React.FC<GraphEdgeProps> = t0 => {
  const $ = _c(43);
  const {
    treeName,
    sourceId,
    targetId,
    label,
    isDirected
  } = t0;
  const theme = useTheme();
  let t1;
  if ($[0] !== sourceId || $[1] !== treeName) {
    t1 = selectNodeDataById(treeName, sourceId);
    $[0] = sourceId;
    $[1] = treeName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const source = useAppSelector(t1);
  let t2;
  if ($[3] !== targetId || $[4] !== treeName) {
    t2 = selectNodeDataById(treeName, targetId);
    $[3] = targetId;
    $[4] = treeName;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const target = useAppSelector(t2);
  if (!source || !target) {
    return null;
  }
  const x1 = source.x + NODE_OFFSET;
  const y1 = source.y + NODE_OFFSET;
  const x2 = target.x + NODE_OFFSET;
  const y2 = target.y + NODE_OFFSET;
  let t3;
  if ($[6] !== x1 || $[7] !== x2 || $[8] !== y1 || $[9] !== y2) {
    t3 = Math.hypot(x2 - x1, y2 - y1);
    $[6] = x1;
    $[7] = x2;
    $[8] = y1;
    $[9] = y2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const distance = t3;
  const angle = Math.atan2(y2 - y1, x2 - x1) * (180 / Math.PI);
  let t4;
  if ($[11] !== source.color || $[12] !== theme) {
    t4 = getNodeColors(theme, source.color);
    $[11] = source.color;
    $[12] = theme;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const sourceColor = t4.nodeColor;
  let t5;
  if ($[14] !== target.color || $[15] !== theme) {
    t5 = getNodeColors(theme, target.color);
    $[14] = target.color;
    $[15] = theme;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const targetColor = t5.nodeColor;
  const gradient = `linear-gradient(to right, ${sourceColor}, ${targetColor})`;
  const t6 = y1 - THICKNESS / 2;
  const t7 = `rotate(${angle}deg)`;
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      opacity: 1
    };
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const t9 = `rotate(${-angle}deg)`;
  let t10;
  if ($[18] !== t9) {
    t10 = {
      transform: t9
    };
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== distance || $[21] !== gradient || $[22] !== t10 || $[23] !== t6 || $[24] !== t7 || $[25] !== x1) {
    t11 = {
      position: "absolute",
      height: THICKNESS,
      transformOrigin: "0 50%",
      width: distance,
      top: t6,
      left: x1,
      transform: t7,
      transition: "opacity 0.3s",
      background: gradient,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      opacity: 0.5,
      "&:hover": t8,
      span: t10
    };
    $[20] = distance;
    $[21] = gradient;
    $[22] = t10;
    $[23] = t6;
    $[24] = t7;
    $[25] = x1;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== isDirected || $[28] !== targetColor) {
    t12 = isDirected ? <Box sx={{
      position: "absolute",
      width: 0,
      height: 0,
      borderLeft: "8px solid transparent",
      borderRight: "8px solid transparent",
      borderTop: `8px solid ${targetColor}`,
      top: "50%",
      right: 16,
      transform: "translateY(-50%) rotate(-90deg) scaleY(2)"
    }} /> : null;
    $[27] = isDirected;
    $[28] = targetColor;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t11 || $[31] !== t12) {
    t13 = <Box sx={t11}>{t12}</Box>;
    $[30] = t11;
    $[31] = t12;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== angle || $[34] !== label || $[35] !== x1 || $[36] !== x2 || $[37] !== y1 || $[38] !== y2) {
    t14 = label ? <Typography component="span" sx={{
      position: "absolute",
      top: (y1 + y2) / 2,
      left: (x1 + x2) / 2,
      transform: `translate(-50%, -50%) rotate(${angle > 100 || angle < -100 ? angle + 180 : angle}deg)`,
      fontSize: 14,
      textShadow: "0 0 6px rgba(255, 255, 255, 0.7)",
      pointerEvents: "none"
    }}>{label}</Typography> : null;
    $[33] = angle;
    $[34] = label;
    $[35] = x1;
    $[36] = x2;
    $[37] = y1;
    $[38] = y2;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t13 || $[41] !== t14) {
    t15 = <>{t13}{t14}</>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
};